import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { memo, useMemo } from 'react';

import { useTimeAgo } from '../../hooks/useTimeAgo';
import type { ToolRegistry } from '../../toolRegistry';
import type {
  ChatMessageItem,
  MessagesGroup,
  ToolCallWithStatus,
} from '../../types';
import { Markdown } from '../Markdown';
import { ToolProvider } from '../tools/ToolProvider';
import { type SharedMessageProps, Message } from '../ui';
import { UserAvatar } from '../UserAvatar';

export interface AssistantMessageProps extends SharedMessageProps {
  name?: string;
  avatar?: string;
  toolRegistry?: ToolRegistry;
  isFetching?: boolean;
  isActive?: boolean;
  insert?: (message: string) => void;
  shouldRenderFeedback?: (group: MessagesGroup) => boolean;
  FeedbackComponent?: React.ComponentType<{
    message: ChatMessageItem;
  }>;
}

/**
 * Checks if the group has any renderable content. We use this
 * to determine if we should show the typing indicator.
 */
function groupHasRenderableContent(
  group: MessagesGroup,
  toolRegistry: ToolRegistry | undefined,
): boolean {
  return group.messages.some(message => {
    if (message.content) {
      return true;
    }

    if (message.toolCalls) {
      return message.toolCalls.some(toolCall => {
        if (toolCall.type === 'text') {
          return !!toolCall.content;
        }

        // Only tools with a component in registry are renderable
        if (toolCall.type === 'tool') {
          return !!toolRegistry?.[toolCall.name]?.component;
        }
      });
    }

    return false;
  });
}

export const AssistantMessage = memo(
  ({
    group,
    name,
    avatar,
    toolRegistry,
    position = 'left',
    variant = 'secondary',
    FeedbackComponent,
    shouldRenderFeedback,
    isFetching,
    isActive,
  }: AssistantMessageProps) => {
    const { messages, createdAt, isLast, isInitial } = group;
    const timeAgo = useTimeAgo(createdAt);
    const firstMessage = messages.at(-1);
    const hasContent = useMemo(
      () => groupHasRenderableContent(group, toolRegistry),
      [group, toolRegistry],
    );

    return (
      <Message.Root variant={variant} position={position}>
        <Message.Header className='flex items-center gap-1'>
          <span>{name}</span>
          <AnimatePresence>
            {isActive && (
              <motion.span
                className='text-xs text-gray-500'
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <Loader2 className='animate-spin size-3' strokeWidth={3} />
              </motion.span>
            )}
          </AnimatePresence>
        </Message.Header>
        <Message.Wrapper>
          <Message.Sidebar>
            <Message.Avatar>
              <UserAvatar src={avatar} />
            </Message.Avatar>
          </Message.Sidebar>
          <Message.Content>
            {hasContent ? (
              messages.map(message => {
                // Render message content when defined
                if (message.content) {
                  return (
                    <Message.BubbleGroup key={message.id}>
                      <Message.Bubble>
                        <Markdown>{message.content}</Markdown>
                      </Message.Bubble>
                    </Message.BubbleGroup>
                  );
                }

                // Don't continue with tool rendering if there is no registry
                if (!toolRegistry) {
                  return null;
                }

                // Render tool calls when defined
                if (message.toolCalls) {
                  return message.toolCalls.map(toolCall => {
                    /**
                     * FIXME the text tool is deprecated, it should not be used with new architecture,
                     * however old conversations have to be migrated before we can remove it.
                     */
                    if (toolCall.type === 'text') {
                      return (
                        <Message.BubbleGroup key={message.id}>
                          <Message.Bubble>
                            <Markdown>{toolCall.content}</Markdown>
                          </Message.Bubble>
                        </Message.BubbleGroup>
                      );
                    }

                    if (toolCall.type === 'tool') {
                      return (
                        <ToolProvider
                          status={
                            (toolCall as ToolCallWithStatus).status ??
                            message.status
                          }
                          args={toolCall.args}
                          toolName={toolCall.name}
                          index={toolCall.index}
                          content={toolCall.content}
                          artifact={toolCall.artifact}
                          toolCallId={toolCall.id}
                          toolRegistry={toolRegistry}
                          key={toolCall.id}
                        />
                      );
                    }
                  });
                }

                return null;
              })
            ) : (
              <Message.BubbleGroup>
                <Message.Typing />
              </Message.BubbleGroup>
            )}

            <AnimatePresence mode='wait'>
              {FeedbackComponent &&
                firstMessage &&
                /**
                 * For the last group, in all chat history, we don't show the feedback
                 * until the stream is finished.
                 */
                !(isFetching && isLast) &&
                (shouldRenderFeedback?.(group) ?? true) && (
                  <motion.div
                    initial={
                      !isFetching && !isInitial && isLast
                        ? { opacity: 0, y: 2 }
                        : false
                    }
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.2,
                      duration: 0.2,
                    }}
                  >
                    <FeedbackComponent message={firstMessage} />
                  </motion.div>
                )}
            </AnimatePresence>
          </Message.Content>
        </Message.Wrapper>
        <Message.Footer>{timeAgo}</Message.Footer>
      </Message.Root>
    );
  },
);
